/**
 * @module PluginManager
 */

 const PluginManager = {

    /**
     * Plugin registry. Plugins should be identified by unique keys.
     * @private
     */
    _plugins: {},

    /**
     * Map from a `gamefroot-4` id to the key name of a plugin.
     * @private
     */
    _pluginsById: {},

    /**
     * Register a script in `gamefroot-4` format so it can be called
     * by internal processes.
     * This creates a reference by ID.
     * @param script - `gamefroot-4` Script resource
     */
    registerScript: function( script ) {
        const javascript = script.javascript;
        if ( typeof javascript !== "string" ) {
            return console.error( "Script was not compiled:", script );
        }

        // Get the name from the data.
        let extractedName = javascript.match( /class ([^ ]+) extends/ );
        if ( !extractedName || !extractedName[ 1 ] ) {
            return console.error( "Could not extract script name:", script );
        }
        extractedName = extractedName[ 1 ];

        // Run the script.
        try {
            // This should automatically call `register`, and add the script
            // to `this._plugins`.
            eval(javascript);
        } catch ( error ) {
            console.error( "Evaluation failure in script:", script );
            console.error( error );
            return;
        }

        // Register the new plugin by script ID.
        const plugin = this._plugins[ extractedName ];
        if ( !plugin ) {
            return console.error( "Script did not create a plugin!" );
        }
        this._pluginsById[ script.id ] = extractedName;
    },

    /**
     * Register a plugin so it can be called by internal processes.
     * @param {string} key - unique plugin name
     * @param {object} plugin - plugin to register
     */
    register: function( key, plugin ) {
        if ( this._plugins[ key ] ) {
            console.warn(
                "PluginManager: Overwriting existing plugin",
                key );
        }
        this._plugins[ key ] = plugin;
    },

    /**
     * Add the designated plugin as a `Component` to the target.
     * @param {string | number} key - plugin to apply.
     *  String is the plugin name.
     *  Number is the plugin ID.
     * @param {GameSprite|Level} target - object to receive plugin
     * @param {object} instanceProperties - custom properties
     */
    instantiate: function( key, target, instanceProperties ) {
        if ( typeof key === "number" ) {
            // Convert it to the name.
            key = this._pluginsById[ key ];
        }

        const plugin = this._plugins[ key ];
        if ( !plugin ) {
            console.error( "PluginManager: Could not find plugin", key );

            const scene = target && target.scene;
            Phaserfroot.game.reportError(
                "Could not find a plugin for a game object.\n" +
                "\n" +
                "Plugin key: " + key + "\n" +
                "Current level: " +
                    ( ( scene && scene.json && scene.json.name ) ||
                    "Unknown" ) + "\n" +
                "Objects beneath this: " + ( ( scene && scene.children && scene.children.list && scene.children.list.length - 1 ) || "0/Unknown" ) + "\n" +
                "X position: " + ( target ? target.posX : "Unknown" ) + "\n" +
                "Y position: " + ( target ? target.posY : "Unknown" )
            );

            return;
        }
        const componentManager = target.components;
        if ( !componentManager ) {
            console.error(
                "PluginManager: Could not add plugin to an object " +
                "without a ComponentManager." );
            Phaserfroot.game.reportError(
                "Could not add plugin to an object.\n" +
                "\n" +
                "The object was missing a component manager. " +
                    "Has a script been attached to a non-game object?\n" +
                "\n" +
                "Plugin key: " + key + "\n" +
                "Current level: " +
                    ( ( scene && scene.json && scene.json.name ) ||
                    "Unknown" ) + "\n" +
                "Objects beneath this: " + ( ( scene && scene.children && scene.children.list && scene.children.list.length - 1 ) || "0/Unknown" ) + "\n" +
                "X position: " + ( ( target && target.x ) || "Unknown" ) + "\n" +
                "Y position: " + ( ( target && target.y ) || "Unknown" )
            );
            return;
        }

        const component = new plugin( target, instanceProperties );
        componentManager.add( component );
    },
 };

 export default PluginManager;
